<template>
  <article id="membership-payment" class="self-service-card membership-payment-card">
    <div v-if="paymentErrorMessage" class="payment-error-modal" role="alertdialog" aria-modal="true">
      <div class="payment-error-modal__backdrop" @click="closePaymentError"></div>
      <div class="payment-error-modal__dialog">
        <strong class="payment-error-modal__title">凭证提交失败</strong>
        <p>{{ paymentErrorMessage }}</p>
        <button type="button" class="payment-error-modal__button" @click="closePaymentError">我知道了</button>
      </div>
    </div>

    <div class="payment-card-head">
      <div class="payment-card-head__title">
        <h3>会员费缴费</h3>
        <p class="payment-card-head__desc">
          {{ showSettledPermanent ? '您已是终身会员，无需再缴会费' : '确认金额后按线下汇款流程上传凭证与发票' }}
        </p>
      </div>
      <div
        v-if="showMembershipFeeActions"
        class="membership-fee-actions"
      >
        <button
          v-if="front.canUpgradeToPermanent"
          type="button"
          class="primary-action compact"
          @click="startUpgradePermanent"
        >
          升级终身会员
        </button>
        <button
          v-if="front.isActiveMember && front.canRenewMembershipFee && !front.isPermanentMember"
          type="button"
          class="secondary-action compact"
          @click="startEarlyRenew"
        >
          提前续费
        </button>
        <button
          v-else-if="front.isExpiredMember && front.canRenewMembershipFee"
          type="button"
          class="primary-action compact"
          @click="startEarlyRenew"
        >
          立即续费
        </button>
      </div>
    </div>

    <!-- 终身会员已生效：只展示完成态，不展示缴费流程与年费说明 -->
    <template v-if="showSettledPermanent">
      <div class="payment-status-panel is-confirmed is-permanent">
        <div class="payment-status-panel__main">
          <strong>终身会员</strong>
          <span>会费已通过终审，长期有效，无需缴纳年费或再次购买终身会费。</span>
        </div>
        <span
          v-if="settledDocPayment && (settledDocPayment.voucherUrl || settledDocPayment.invoiceUrl)"
          class="payment-doc-links"
        >
          <a
            v-if="settledDocPayment.voucherUrl"
            href="javascript:;"
            @click.prevent="front.openSecureFile(settledDocPayment.voucherUrl)"
          >查看凭证</a>
          <a
            v-if="settledDocPayment.invoiceUrl"
            href="javascript:;"
            @click.prevent="front.openSecureFile(settledDocPayment.invoiceUrl)"
          >查看发票</a>
        </span>
      </div>
    </template>

    <template v-else>
      <ol class="membership-fee-steps" aria-label="会费缴纳流程">
        <li
          v-for="step in feeSteps"
          :key="step.key"
          :class="stepItemClass(step.key)"
        >
          <span class="membership-fee-steps__index">{{ step.index }}</span>
          <span class="membership-fee-steps__label">{{ step.label }}</span>
        </li>
      </ol>

      <div class="service-notice payment-fee-summary">
        <strong>{{ front.membershipFeeCategoryLabel }} · {{ front.membershipFeeCurrencySymbol }}{{ front.membershipFeeAmount }}{{ front.membershipFeeAmountSuffix }}</strong>
        <span v-if="front.paymentFeeChoice === 'PERMANENT'">一次缴清终身会费，审核通过后长期有效，无需再缴年费。</span>
        <span v-else-if="front.isCouncilMemberPath">理事会费按本届最高一档收取；若兼任多项职务，只需缴纳一次。审核通过后有效期至本届任期结束；任期结束后须经秘书处确认，方可转为终身会员。</span>
        <span v-else-if="front.isForeignMemberPath">外籍会员会费按美元收取。会费按自然年计算，请于每年 8 月 31 日前缴纳当年会费；审核通过后有效期至该年 12 月 31 日。累计缴纳达到规定金额后，将自动升级为终身会员。</span>
        <span v-else>学生会员与普通会员年费金额相同。会费按自然年计算，请于每年 8 月 31 日前缴纳当年会费；审核通过后有效期至该年 12 月 31 日。累计缴纳达到规定金额后，将自动升级为终身会员。</span>
      </div>

      <div
        v-if="canAccessPayment && !front.isCouncilMemberPath && showFeeChoice && (front.permanentFeeEnabled || front.canUpgradeToPermanent)"
        class="fee-choice-panel"
      >
        <strong>会费类型</strong>
        <label v-if="!front.canUpgradeToPermanent || front.canPayAnnualFee" class="fee-choice-option">
          <input v-model="front.paymentFeeChoice" type="radio" value="ANNUAL" />
          年费个人（学生会员 / 普通会员）
        </label>
        <label v-if="front.permanentFeeEnabled && (front.canPayPermanentFee || front.canUpgradeToPermanent)" class="fee-choice-option">
          <input v-model="front.paymentFeeChoice" type="radio" value="PERMANENT" />
          {{ front.canUpgradeToPermanent && !front.canPayAnnualFee ? '升级为终身会员' : '终身会员（一次缴清）' }}
        </label>
      </div>

      <div v-if="!canAccessPayment && front.isLoggedIn" class="service-notice warning">
        <strong>暂不可缴费</strong>
        <span>请先提交入会申请并通过秘书处审核后，再开始缴纳会费。</span>
      </div>

      <button
        v-if="showStartButton"
        type="button"
        class="primary-action compact"
        @click="front.submitPayment"
      >
        {{ startButtonLabel }}
      </button>

      <template v-if="canAccessPayment && payment">
        <div v-if="showBankInfo" class="offline-payment-card">
          <strong>线下转账说明</strong>
          <span>{{ front.offlinePaymentInfo.account }}</span>
          <span>{{ front.offlinePaymentInfo.remark }}</span>
          <span>{{ front.offlinePaymentInfo.upload }}</span>
        </div>

        <div v-if="payment.paymentStatus === 'VOUCHER_REVIEW'" class="payment-status-panel is-review">
          <strong>凭证初审中</strong>
          <span>您的缴费凭证已提交，请等待管理员审核。审核通过后将进入发票上传阶段。</span>
        </div>

        <div v-else-if="payment.paymentStatus === 'VOUCHER_REJECTED'" class="payment-status-panel is-rejected">
          <strong>凭证已驳回</strong>
          <span v-if="payment.reviewComment">驳回原因：{{ payment.reviewComment }}</span>
          <span>请核对汇款信息后重新上传凭证。</span>
        </div>

        <div v-if="canUploadVoucher" class="payment-flow__upload">
          <strong>第 3 步：上传缴费凭证</strong>
          <span>请先选择凭证文件，确认无误后再提交。提交后将进入凭证初审，审核期间不可修改。</span>
          <label class="application-file-picker payment-file-picker">
            <span>凭证文件（JPG/PNG，≤ 5MB）</span>
            <input
              ref="voucherFileInput"
              type="file"
              accept=".jpg,.jpeg,.png"
              @change="selectVoucherFile"
            />
            <small v-if="pendingVoucherFile">已选择：{{ pendingVoucherFile.name }}</small>
          </label>
          <div class="inline-actions">
            <button
              v-if="canCancelDraft"
              type="button"
              class="secondary-action compact"
              :disabled="cancellingDraft || submittingVoucher"
              @click="cancelDraft"
            >
              {{ cancellingDraft ? '正在取消...' : '暂不缴费' }}
            </button>
            <button
              v-if="pendingVoucherFile"
              type="button"
              class="secondary-action compact"
              @click="clearVoucherFile"
            >
              重新选择
            </button>
            <button
              type="button"
              class="primary-action compact"
              :disabled="!pendingVoucherFile || submittingVoucher || cancellingDraft"
              @click="submitVoucher"
            >
              {{ submittingVoucher ? '正在提交...' : '确认提交凭证' }}
            </button>
          </div>
        </div>

        <div v-if="payment.paymentStatus === 'INVOICE_REVIEW'" class="payment-status-panel is-review">
          <strong>发票终审中</strong>
          <span>您的电子发票已提交，请等待财务终审。通过后您将成为正式会员。</span>
        </div>

        <div v-else-if="payment.paymentStatus === 'INVOICE_REJECTED'" class="payment-status-panel is-rejected">
          <strong>发票已驳回</strong>
          <span v-if="payment.reviewComment">驳回原因：{{ payment.reviewComment }}</span>
          <span>请重新上传电子发票。</span>
        </div>

        <div v-if="canUploadInvoice" class="payment-flow__upload">
          <strong>第 4 步：上传电子发票</strong>
          <span>
            凭证初审已通过，请在
            <template v-if="payment.invoiceDeadline"> {{ front.formatDate(payment.invoiceDeadline) }} 前</template>
            选择发票文件并确认提交。提交后将进入发票终审，审核期间不可修改。
          </span>
          <label class="application-file-picker payment-file-picker">
            <span>发票文件（JPG/PNG/PDF，≤ 10MB）</span>
            <input
              ref="invoiceFileInput"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              @change="selectInvoiceFile"
            />
            <small v-if="pendingInvoiceFile">已选择：{{ pendingInvoiceFile.name }}</small>
          </label>
          <div class="inline-actions">
            <button
              v-if="pendingInvoiceFile"
              type="button"
              class="secondary-action compact"
              @click="clearInvoiceFile"
            >
              重新选择
            </button>
            <button
              type="button"
              class="primary-action compact"
              :disabled="!pendingInvoiceFile || submittingInvoice"
              @click="submitInvoice"
            >
              {{ submittingInvoice ? '正在提交...' : '确认提交发票' }}
            </button>
          </div>
        </div>

        <div v-if="payment.paymentStatus === 'INVOICE_OVERDUE'" class="payment-status-panel is-overdue">
          <strong>发票逾期未传</strong>
          <span>已超过发票上传截止日，会员资格暂时锁定，请尽快上传电子发票。</span>
        </div>

        <div v-if="showConfirmedCurrentStatus" class="payment-status-panel is-confirmed">
          <div class="payment-status-panel__main">
            <strong>{{ confirmedStatusTitle }}</strong>
            <span>{{ confirmedStatusDesc }}</span>
            <span v-if="isPermanentPayment">长期有效</span>
            <span v-else-if="currentMemberValidEndDate">会员有效期至 {{ front.formatDate(currentMemberValidEndDate) }}</span>
          </div>
          <span v-if="payment.voucherUrl || payment.invoiceUrl" class="payment-doc-links">
            <a v-if="payment.voucherUrl" href="javascript:;" @click.prevent="front.openSecureFile(payment.voucherUrl)">查看凭证</a>
            <a v-if="payment.invoiceUrl" href="javascript:;" @click.prevent="front.openSecureFile(payment.invoiceUrl)">查看发票</a>
          </span>
        </div>
      </template>

      <div v-else-if="!showStartButton && !front.isLoggedIn" class="service-notice warning">
        请先登录后办理会费缴纳。
      </div>
    </template>
  </article>
</template>

<script>
const FEE_STEPS = [
  { key: 'amount', index: 1, label: '确认金额' },
  { key: 'transfer', index: 2, label: '银行汇款' },
  { key: 'voucher', index: 3, label: '上传凭证' },
  { key: 'invoice', index: 4, label: '上传发票' }
]

export default {
  name: 'MembershipPaymentCard',
  inject: ['front'],
  data() {
    return {
      pendingVoucherFile: null,
      pendingInvoiceFile: null,
      paymentErrorMessage: '',
      submittingVoucher: false,
      submittingInvoice: false,
      cancellingDraft: false
    }
  },
  watch: {
    'payment.paymentId'() {
      this.resetPendingFiles()
    },
    'payment.paymentStatus'() {
      this.resetPendingFiles()
    }
  },
  computed: {
    canAccessPayment() {
      return this.front.canAccessMembershipPayment
    },
    feeSteps() {
      return FEE_STEPS
    },
    payment() {
      return this.front.activeMembershipPayment
    },
    /** 已是终身会员且当前无在途缴费单：只展示完成态 */
    showSettledPermanent() {
      return this.front.isPermanentMember && !this.front.hasInProgressMembershipPayment
    },
    settledDocPayment() {
      if (!this.showSettledPermanent) return null
      const list = this.front.confirmedMembershipPayments || []
      const permanent = list.find(item => String(item.memberCategory || '').toUpperCase() === 'PERMANENT')
      return permanent || list[0] || this.payment || null
    },
    isPermanentPayment() {
      const category = String((this.payment && this.payment.memberCategory) || '').toUpperCase()
      return this.front.isPermanentMember || category === 'PERMANENT'
    },
    confirmedStatusTitle() {
      return this.isPermanentPayment ? '终身会员' : '正式会员'
    },
    confirmedStatusDesc() {
      return this.isPermanentPayment
        ? '本次会费已通过终审，您已成为终身会员。'
        : '本次会费已通过终审，您已成为正式会员。'
    },
    /** 已过期的缴费单属于历史记录，不再作为当前会员状态展示。 */
    showConfirmedCurrentStatus() {
      return Boolean(this.payment && this.payment.paymentStatus === 'CONFIRMED'
        && (this.front.isActiveMember || this.front.isPermanentMember))
    },
    /** 当前资格以会员档案为准；缴费单日期仅作为旧数据兼容回退。 */
    currentMemberValidEndDate() {
      return this.front.memberValidEndDate || (this.payment && this.payment.validEndDate) || ''
    },
    showStartButton() {
      if (this.front.isPermanentMember) {
        return false
      }
      return (this.front.canPayMembershipFee || this.front.canRenewMembershipFee || this.front.canUpgradeToPermanent)
        && !this.front.hasInProgressMembershipPayment
    },
    /** 身份已在左侧栏展示；此处仅在可续费/升级时保留快捷操作 */
    showMembershipFeeActions() {
      if (this.front.hasInProgressMembershipPayment) {
        return false
      }
      if (this.front.canUpgradeToPermanent) {
        return true
      }
      if (this.front.isActiveMember && this.front.canRenewMembershipFee && !this.front.isPermanentMember) {
        return true
      }
      if (this.front.isExpiredMember && this.front.canRenewMembershipFee) {
        return true
      }
      return false
    },
    showFeeChoice() {
      return this.showStartButton || (this.payment && this.payment.paymentStatus === 'UNPAID')
    },
    startButtonLabel() {
      if (this.front.paymentFeeChoice === 'PERMANENT') {
        return this.front.canUpgradeToPermanent && !this.front.canPayAnnualFee
          ? '开始升级为终身会员'
          : '开始缴纳终身会费'
      }
      if (this.front.isActiveMember && this.front.canRenewMembershipFee) {
        return '开始提前续费'
      }
      if (this.front.canRenewMembershipFee) {
        return '开始续费'
      }
      return '开始缴纳会费'
    },
    showBankInfo() {
      if (!this.payment) {
        return false
      }
      return ['UNPAID', 'VOUCHER_REJECTED', 'VOUCHER_REVIEW'].includes(this.payment.paymentStatus)
    },
    canUploadVoucher() {
      if (!this.payment) {
        return false
      }
      return ['UNPAID', 'VOUCHER_REJECTED'].includes(this.payment.paymentStatus)
    },
    canCancelDraft() {
      return !!(this.payment && this.payment.paymentStatus === 'UNPAID')
    },
    canUploadInvoice() {
      if (!this.payment) {
        return false
      }
      return ['INVOICE_PENDING', 'INVOICE_REJECTED', 'INVOICE_OVERDUE'].includes(this.payment.paymentStatus)
    },
    stepProgressLevel() {
      if (this.showStartButton || !this.payment) {
        return 1
      }
      const map = {
        UNPAID: 3,
        VOUCHER_REJECTED: 3,
        VOUCHER_REVIEW: 3.5,
        INVOICE_PENDING: 4,
        INVOICE_OVERDUE: 4,
        INVOICE_REJECTED: 4,
        INVOICE_REVIEW: 4.5,
        CONFIRMED: 5
      }
      return map[this.payment.paymentStatus] || 1
    }
  },
  methods: {
    startUpgradePermanent() {
      this.front.paymentFeeChoice = 'PERMANENT'
      this.front.submitPayment()
    },
    startEarlyRenew() {
      this.front.paymentFeeChoice = 'ANNUAL'
      this.front.submitPayment()
    },
    resetPendingFiles() {
      this.pendingVoucherFile = null
      this.pendingInvoiceFile = null
      if (this.$refs.voucherFileInput) {
        this.$refs.voucherFileInput.value = ''
      }
      if (this.$refs.invoiceFileInput) {
        this.$refs.invoiceFileInput.value = ''
      }
    },
    selectVoucherFile(event) {
      const file = event.target.files[0]
      if (!file) return
      if (!this.front.validateSelectedFile(file, 'payment', 'voucher')) {
        event.target.value = ''
        this.pendingVoucherFile = null
        return
      }
      this.pendingVoucherFile = file
    },
    selectInvoiceFile(event) {
      const file = event.target.files[0]
      if (!file) return
      if (!this.front.validateSelectedFile(file, 'payment', 'invoice')) {
        event.target.value = ''
        this.pendingInvoiceFile = null
        return
      }
      this.pendingInvoiceFile = file
    },
    clearVoucherFile() {
      this.pendingVoucherFile = null
      if (this.$refs.voucherFileInput) {
        this.$refs.voucherFileInput.value = ''
      }
    },
    clearInvoiceFile() {
      this.pendingInvoiceFile = null
      if (this.$refs.invoiceFileInput) {
        this.$refs.invoiceFileInput.value = ''
      }
    },
    closePaymentError() {
      this.paymentErrorMessage = ''
    },
    async submitVoucher() {
      if (!this.pendingVoucherFile) return
      this.paymentErrorMessage = ''
      this.submittingVoucher = true
      try {
        const submitted = await this.front.submitMembershipPaymentFile('voucher', this.pendingVoucherFile)
        if (submitted) {
          this.clearVoucherFile()
        } else if (this.front.selfServiceStatus) {
          this.paymentErrorMessage = this.front.selfServiceStatus
        }
      } finally {
        this.submittingVoucher = false
      }
    },
    async cancelDraft() {
      if (!this.payment || !this.canCancelDraft) return
      this.cancellingDraft = true
      try {
        await this.front.cancelMembershipPayment(this.payment.paymentId)
        this.resetPendingFiles()
      } finally {
        this.cancellingDraft = false
      }
    },
    async submitInvoice() {
      if (!this.pendingInvoiceFile) return
      this.submittingInvoice = true
      try {
        const submitted = await this.front.submitMembershipPaymentFile('invoice', this.pendingInvoiceFile)
        if (submitted) {
          this.clearInvoiceFile()
        }
      } finally {
        this.submittingInvoice = false
      }
    },
    stepItemClass(stepKey) {
      const thresholds = { amount: 1, transfer: 2, voucher: 3, invoice: 4 }
      const level = this.stepProgressLevel
      const stepLevel = thresholds[stepKey]
      return {
        active: level >= stepLevel && level < stepLevel + 1,
        done: level >= stepLevel + 1 || level >= 5
      }
    }
  }
}
</script>

<style scoped>
.payment-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 16px;
  width: 100%;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(31, 79, 122, 0.08);
}

.payment-card-head__title {
  display: grid;
  gap: 4px;
  min-width: 0;
}

.payment-card-head__title h3 {
  margin: 0;
}

.payment-card-head__desc {
  margin: 0;
  color: #5f6670;
  font-size: 13px;
  line-height: 1.5;
}

.membership-fee-steps {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0;
  margin: 4px 0 6px;
  padding: 14px 8px 10px;
  list-style: none;
  border-radius: 10px;
  background: linear-gradient(180deg, #f7fafc 0%, #f2f6f9 100%);
  border: 1px solid rgba(31, 79, 122, 0.08);
}

.membership-fee-steps li {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  gap: 8px;
  text-align: center;
  color: #8a93a0;
  font-size: 12px;
  line-height: 1.35;
}

.membership-fee-steps li:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 14px;
  left: calc(50% + 18px);
  right: calc(-50% + 18px);
  height: 2px;
  background: #e2e8f0;
  z-index: 0;
}

.membership-fee-steps li.done:not(:last-child)::after {
  background: #b7dcc5;
}

.membership-fee-steps li.active:not(:last-child)::after {
  background: linear-gradient(90deg, #002b49 0%, #e2e8f0 100%);
}

.membership-fee-steps__index {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1.5px solid #d5dde6;
  background: #fff;
  font-weight: 700;
  font-size: 12px;
  color: #6b7280;
  box-shadow: 0 1px 2px rgba(14, 38, 67, 0.06);
}

.membership-fee-steps__label {
  font-weight: 600;
}

.membership-fee-steps li.active {
  color: #002b49;
}

.membership-fee-steps li.active .membership-fee-steps__index {
  border-color: #002b49;
  background: #002b49;
  color: #fff;
  box-shadow: 0 4px 10px rgba(0, 43, 73, 0.22);
}

.membership-fee-steps li.done {
  color: #1d6b3a;
}

.membership-fee-steps li.done .membership-fee-steps__index {
  border-color: #86c99a;
  background: #f0f9eb;
  color: #1d6b3a;
}

.payment-fee-summary {
  width: 100%;
  max-width: none;
  border-color: rgba(31, 79, 122, 0.12);
  background: #f5f9fc;
  color: #3d4a57;
}

.payment-fee-summary strong {
  color: #002b49;
  font-size: 15px;
}

.payment-status-panel {
  margin-top: 4px;
  padding: 14px 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  line-height: 1.6;
  width: 100%;
  max-width: none;
  box-sizing: border-box;
}

.payment-status-panel strong {
  font-size: 14px;
}

.payment-status-panel.is-review {
  background: #fdf8f0;
  border: 1px solid #f0dfc4;
  color: #9a6b2f;
}

.payment-status-panel.is-rejected {
  background: #fff5f5;
  border: 1px solid #f5d0d0;
  color: #c0392b;
}

.payment-status-panel.is-overdue {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #c2410c;
}

.payment-status-panel.is-confirmed {
  background: #f3faf5;
  border: 1px solid #cfe8d5;
  color: #1d6b3a;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px 28px;
  flex-wrap: wrap;
}

.payment-status-panel.is-permanent {
  justify-content: center;
  text-align: center;
}

.payment-status-panel.is-permanent .payment-status-panel__main {
  align-items: center;
}

.payment-status-panel.is-permanent .payment-doc-links {
  justify-content: center;
  width: 100%;
}

.payment-status-panel__main {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.payment-doc-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  flex: 0 0 auto;
  align-items: center;
  padding-top: 2px;
}

.payment-doc-links a {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
}

.payment-flow__upload {
  margin-top: 4px;
  padding: 14px 16px;
  border: 1px solid rgba(31, 79, 122, 0.12);
  border-radius: 8px;
  background: #fafcfd;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  color: #303133;
}

.payment-file-picker {
  margin-top: 4px;
}

.membership-payment-card > .primary-action.compact {
  min-width: 168px;
  margin-top: 2px;
}

.membership-fee-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.fee-choice-panel {
  margin: 0;
  padding: 14px 16px;
  border: 1px solid rgba(31, 79, 122, 0.12);
  border-radius: 8px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;
}

.fee-choice-panel > strong {
  color: #002b49;
  font-size: 13px;
}

.fee-choice-option {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid rgba(31, 79, 122, 0.12);
  border-radius: 8px;
  background: #f8fbff;
  cursor: pointer;
  font-weight: 500;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.fee-choice-option:has(input:checked) {
  border-color: rgba(0, 43, 73, 0.35);
  background: #eef5fb;
}

.fee-choice-option input[type='radio'] {
  accent-color: #002b49;
}
</style>
